<template>
  <div
    ref="contentRoot"
    class="tw-report"
    v-html="analysisContent"
  />
</template>

<script>
import Vue from 'vue';
import ReportImageCarousel from '@/analytics/components/modules/ReportImageCarousel.vue';

export default {
  name: 'ContentTemplate',

  props: {
    analysisContent: {
      type: String,
      default: '',
    },

    images: {
      type: Array,
      default: () => [],
    },
  },

  watch: {
    analysisContent() {
      this.mountDynamicComponents();
    },

    images: {
      handler() {
        this.mountDynamicComponents();
      },
      deep: true,
    },
  },

  mounted() {
    this.mountDynamicComponents();
  },

  beforeDestroy() {
    this.destroyDynamicComponents();
  },

  methods: {
    parseJsonAttr(el, attrName, fallback = []) {
      try {
        const value = el.getAttribute(attrName);

        if (!value) {
          return fallback;
        }

        return JSON.parse(value);
      } catch (e) {
        console.warn(`Invalid JSON in ${attrName}`, e);
        return fallback;
      }
    },

    parseBoolAttr(el, attrName, fallback = false) {
      const value = el.getAttribute(attrName);

      if (value === null) {
        return fallback;
      }

      return value === 'true' || value === '1';
    },

    getImageSrc(image) {
      if (!image) {
        return null;
      }

      if (typeof image === 'string') {
        return image;
      }

      return image.src || image.url || image.image_url || image.image || null;
    },

    getImagesArray() {
      return (this.images || [])
        .map(image => this.getImageSrc(image))
        .filter(Boolean);
    },

    escapeHtmlAttr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    mountHeroProductImage() {
      const root = this.$refs.contentRoot;

      if (!root) {
        return;
      }

      const heroImage = root.querySelector('[data-component="hero-product-image"]');

      if (!heroImage) {
        return;
      }

      const imagesArr = this.getImagesArray();
      const heroSrc = imagesArr[0] || null;

      if (!heroSrc) {
        return;
      }

      heroImage.classList.add('overflow-hidden');

      heroImage.innerHTML = `
        <img
          src="${this.escapeHtmlAttr(heroSrc)}"
          alt="Main image"
        >
      `;
    },

    getReportImageCarouselProps(el) {
      const imagesArr = this.getImagesArray();

      return {
        title: el.dataset.title || 'Recommended image carousel',

        images: imagesArr,

        currentImagesOrder: this.parseJsonAttr(el, 'data-current-images-order', []),
        recommendedImagesOrder: this.parseJsonAttr(el, 'data-recommended-images-order', []),
        carouselSlots: this.parseJsonAttr(el, 'data-carousel-slots', []),
        aiImages: this.parseJsonAttr(el, 'data-ai-images', []),

        defaultRecommendedOrder: this.parseBoolAttr(el, 'data-default-recommended-order', true),
        showPreviewCta: this.parseBoolAttr(el, 'data-show-preview-cta', true),
        sourceImageIndex: el.dataset.sourceImageIndex ?? null,
      };
    },

    mountReportImageCarousels() {
      const root = this.$refs.contentRoot;

      if (!root) {
        return;
      }

      root
        .querySelectorAll('[data-component="report-image-carousel"]')
        .forEach((el) => {
          if (el.__vueMounted) {
            return;
          }

          const props = this.getReportImageCarouselProps(el);

          el.__vueMounted = true;

          const instance = new Vue({
            parent: this,
            render: h => h(ReportImageCarousel, { props }),
          });

          instance.$mount(el);

          el.__vueInstance = instance;
        });
    },

    mountDynamicComponents() {
      this.$nextTick(() => {
        this.mountHeroProductImage();
        this.mountReportImageCarousels();
      });
    },

    destroyDynamicComponents() {
      const root = this.$refs.contentRoot;

      if (!root) {
        return;
      }

      root
        .querySelectorAll('[data-component="report-image-carousel"]')
        .forEach((el) => {
          if (el.__vueInstance) {
            el.__vueInstance.$destroy();
            el.__vueInstance = null;
          }

          el.__vueMounted = false;
        });
    },
  },
};
</script>
